import { MaterialIcons } from "@expo/vector-icons";
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FlatList,
  ListRenderItemInfo,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { Badge, useTheme } from "react-native-elements";
import { getUnreadNotificationsByGroup } from "../../backend/Notifications";
import { PlayerContext } from "../../backend/PlayerContext";
import Divider from "../utility/Divider";
import {
  getAllMenuItems,
  useHorizontalScrollRef,
} from "../utility/Miscellaneous";
import { Text } from "../utility/Text";
import { GameIcon } from "../utility/icons/GameIcon";

const SelectedContext = React.createContext("Campus");

function MenuComponent(props: {
  title: string;
  showBadge: boolean;
  onPress: () => void;
}) {
  const { title, onPress } = { ...props };
  const theme = useTheme();
  const selected = useContext(SelectedContext);
  const shouldHighlight = selected == title;
  return useMemo(
    () => (
      <TouchableOpacity
        onPress={onPress}
        accessible={true}
        accessibilityLabel={title}
        accessibilityRole="tab"
      >
        <View
          style={[
            styles.itemContainerStyle,
            shouldHighlight
              ? { backgroundColor: theme.theme.colors.grey5 }
              : null,
          ]}
        >
          <View>
            {props.showBadge && (
              <Badge containerStyle={styles.notificationBadge} status="error" />
            )}
            <GameIcon icon={title.toLowerCase()} />
          </View>
          <Text doNotScaleFonts={true} style={styles.itemStyle}>
            {title}
          </Text>
        </View>
      </TouchableOpacity>
    ),
    [title, onPress, shouldHighlight, theme, props.showBadge],
  );
}

const SCROLL_VIEW_EPSILON = 10;

export function BottomMenu(props: {
  onNavigationPress: (section: string) => void;
  onSpellMenuPress: () => void;
  selected: string;
  leftHanded: boolean;
  showCastSpellButton: boolean;
}) {
  const playerContext = useContext(PlayerContext);

  const menuItemsCalculated = getAllMenuItems(playerContext).filter(
    (item) => !playerContext.global.hiddenFromBottomMenu.includes(item),
  );

  const navigationPressCallback = useCallback(
    (item: string) => {
      props.onNavigationPress(item);
    },
    [props.onNavigationPress],
  );

  const menuItems = useMemo(
    () => menuItemsCalculated,
    [menuItemsCalculated.join()],
  );
  const callbacks = useMemo(
    () => menuItems.map((item) => () => navigationPressCallback(item)),
    [menuItems, navigationPressCallback],
  );
  const sectionsWithBadges = menuItems.filter(
    (group) => getUnreadNotificationsByGroup(playerContext, group).length > 0,
  );

  const renderItem = useCallback(
    (item: ListRenderItemInfo<string>) => (
      <View style={{ flexDirection: "row" }}>
        {item.index === 0 && <Divider orientation="vertical" />}
        <MenuComponent
          title={item.item}
          showBadge={sectionsWithBadges.includes(item.item)}
          onPress={callbacks[item.index]}
        />
        <Divider orientation="vertical" />
      </View>
    ),
    [callbacks, navigationPressCallback, sectionsWithBadges.join()],
  );

  const keyExtractor = useCallback((item: string) => item, []);

  const theme = useTheme();
  const [scrollEnd, setScrollEnd] = useState(false);
  const [scrollStart, setScrollStart] = useState(true);
  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const position = e.nativeEvent.contentOffset.x;
      const contentWidth = e.nativeEvent.contentSize.width;
      const scrollViewWidth = e.nativeEvent.layoutMeasurement.width;
      const isStart = position < SCROLL_VIEW_EPSILON;
      const isEnd =
        contentWidth - scrollViewWidth - position < SCROLL_VIEW_EPSILON;
      if (isStart != scrollStart) {
        setScrollStart(isStart);
      }
      if (isEnd != scrollEnd) {
        setScrollEnd(isEnd);
      }
    },
    [scrollEnd, scrollStart, setScrollEnd, setScrollStart],
  );

  const flatListBackgroundStyle = useMemo(
    () => [
      styles.scrollContentContainer,
      { backgroundColor: theme.theme.colors.background },
    ],
    [theme],
  );

  const wheelScrollRef =
    Platform.OS === "web" ? useHorizontalScrollRef() : useRef();

  return useMemo(
    () => (
      <View
        style={[
          styles.bottomMenuContainer,
          { backgroundColor: theme.theme.colors.grey5 },
        ]}
      >
        {props.leftHanded && props.showCastSpellButton && (
          <View style={styles.spellButtonPlaceholder} />
        )}
        <View style={[styles.chevron, scrollStart && { opacity: 0 }]}>
          <MaterialIcons
            name="chevron-left"
            size={24}
            color={theme.theme.colors.black}
          />
        </View>
        <SelectedContext.Provider value={props.selected}>
          <FlatList
            ref={wheelScrollRef}
            data={menuItems}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            horizontal={true}
            snapToInterval={71}
            style={styles.scrollContainer}
            contentContainerStyle={flatListBackgroundStyle}
            scrollEventThrottle={100}
            onScroll={onScroll}
            overScrollMode="never"
            showsHorizontalScrollIndicator={false}
            nestedScrollEnabled={true}
          />
        </SelectedContext.Provider>
        <View style={[styles.chevron, scrollEnd && { opacity: 0 }]}>
          <MaterialIcons
            name="chevron-right"
            size={24}
            color={theme.theme.colors.black}
          />
        </View>
        {!props.leftHanded && props.showCastSpellButton && (
          <View style={styles.spellButtonPlaceholder} />
        )}
      </View>
    ),
    [
      menuItems,
      renderItem,
      theme,
      scrollStart,
      scrollEnd,
      flatListBackgroundStyle,
      onScroll,
      props.selected,
      props.leftHanded,
      props.showCastSpellButton,
    ],
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    height: 48,
  },
  scrollContentContainer: {
    paddingVertical: 0,
    justifyContent: "center",
  },
  navigationBlock: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  itemContainerStyle: {
    width: 70,
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  itemStyle: {
    fontSize: 10,
  },
  gradient: {
    flex: 1,
  },
  spellButtonPlaceholder: {
    width: 84,
    height: 48,
  },
  bottomMenuContainer: { flexDirection: "row", alignItems: "center" },
  spellMenuButtonPlaceholder: {
    height: 48,
    justifyContent: "flex-end",
    alignItems: "flex-end",
    overflow: "visible",
  },
  chevron: {
    width: 24,
    height: 48,
    justifyContent: "center",
  },
  notificationBadge: {
    position: "absolute",
    top: 4,
    right: 4,
  },
});
